/**
 * SupplierJourneyOrchestrator (AJIP-16.02.008 · Sole Composition Layer)
 * ---------------------------------------------------------------------
 * Coordinates published services only. Never duplicates Registration,
 * Discovery, Eligibility, Resolution, Activation, Core Engine state
 * management, Persistence, or Journey Commands. Contains
 * Supplier-specific configuration and orchestration only.
 *
 * Founder Rulings honoured:
 *
 *   S1  Audience is ['authenticated', 'authorised-supplier'] (enforced
 *       via the SupplierJourneyDefinition).
 *   S2  persistencePolicyRef = 'supplier:v2:standard' (enforced via
 *       the definition).
 *   S3  No `no-reentry-after:completed`. Suppliers may pursue multiple
 *       independent objectives. Distinct objectives get distinct
 *       instance identities (see buildSupplierInstanceId).
 *   S4  VERIFICATION_OUTCOME_RECEIVED is a passive record. The
 *       orchestrator files an outcome via the trust boundary; it does
 *       not manufacture the outcome.
 *   S5  Buyer state is NEVER read. Any Buyer signal must arrive as a
 *       validated opaque reference via BuyerSupplierBoundary.
 *   S6  journeyId ≠ journeyInstanceId. Instance identity is
 *       deterministic (FNV-1a) over canonical inputs, ordered:
 *       principalRef, organisationRef, supplierObjectiveRef,
 *       correlationRef. Raw inputs are never exposed inside the
 *       identifier; only the hash appears.
 *   S7  Organisation ownership evaluated at runtime via a caller-supplied
 *       provider. Ownership data is NEVER embedded in the shared
 *       canonical Supplier Journey Definition.
 *
 * Additional locked directive:
 *   Stage mutation MUST use engine.updateStage() only. Never touch
 *   JourneyContext._setStage() directly.
 */
import { isFeatureEnabled } from '../../lib/featureFlags';
import { FlagDisabledError } from '../core/JourneyErrors';
import { buildSupplierDefinition } from './SupplierJourneyDefinition';
import {
  SUPPLIER_STAGES, SUPPLIER_EVENTS,
  findSupplierTransition, STAGE_TO_ENGINE_STATE,
} from './SupplierJourneyModel';
import { validateSupplierContext } from './SupplierJourneyContextContract';
import {
  SUPPLIER_JOURNEY_EVENTS, SupplierJourneyObservability,
} from './SupplierJourneyObservability';
import {
  InvalidSupplierTransitionError,
  SupplierJourneyNotActiveError,
  SupplierInstanceIdentityError,
} from './SupplierJourneyErrors';
import { recordVerificationOutcome } from './SupplierVerificationTrustBoundary';
import {
  evaluateOwnership, OWNERSHIP_DECISIONS,
} from './SupplierJourneyOwnership';
import { fnv1a } from '../persistence/Hash';

const SUPPLIER_JOURNEY_ID = 'supplier';
const SUPPLIER_JOURNEY_VERSION = 2;

const _requireEnabled = (svc) => {
  if (!svc._enabled) {
    throw new FlagDisabledError('JOURNEYS_ENABLED is false. SupplierJourneyOrchestrator is inert.');
  }
};

/**
 * Founder Ruling S6 — Supplier Journey instance identity.
 *
 * Deterministic hash over canonical inputs, in a canonical order.
 * Required inputs (fail-closed on missing):
 *   - principalRef
 *   - organisationRef
 *   - supplierObjectiveRef
 * Optional:
 *   - correlationRef (defaults to empty string)
 *
 * Raw inputs are NEVER emitted in the identifier — only the FNV-1a
 * digest. Same inputs yield the same identifier (deterministic replay).
 * Any single input change yields a distinct identifier (per-objective
 * separation required by S3).
 */
const buildSupplierInstanceId = ({
  principalRef, organisationRef, supplierObjectiveRef, correlationRef,
}) => {
  const missing = [];
  if (typeof principalRef !== 'string' || principalRef.length === 0) missing.push('principalRef');
  if (typeof organisationRef !== 'string' || organisationRef.length === 0) missing.push('organisationRef');
  if (typeof supplierObjectiveRef !== 'string' || supplierObjectiveRef.length === 0) missing.push('supplierObjectiveRef');
  if (missing.length > 0) {
    throw new SupplierInstanceIdentityError(
      `Missing required identity input(s): ${missing.join(', ')}`,
    );
  }
  const corr = (typeof correlationRef === 'string' && correlationRef.length > 0)
    ? correlationRef : '';
  // Canonical order: principalRef :: organisationRef :: supplierObjectiveRef :: correlationRef.
  const material = `${principalRef}::${organisationRef}::${supplierObjectiveRef}::${corr}`;
  // Prefix distinguishes from journey definition ID (which is 'supplier').
  return `sji_${fnv1a(material)}`;
};

class SupplierJourneyOrchestrator {
  constructor({
    engine, registrationService, discoveryService,
    resolutionService, activationService, journeyCommands,
    persistenceService = null,
    ownershipProvider = null,
    observability = null,
    nowFn = () => Date.now(),
  } = {}) {
    this._enabled = isFeatureEnabled('JOURNEYS_ENABLED');
    if (this._enabled) {
      if (!engine) throw new Error('SupplierJourneyOrchestrator requires engine');
      if (!registrationService) throw new Error('requires registrationService');
      if (!discoveryService) throw new Error('requires discoveryService');
      if (!resolutionService) throw new Error('requires resolutionService');
      if (!activationService) throw new Error('requires activationService');
      if (!journeyCommands) throw new Error('requires journeyCommands');
    }
    this._engine = engine || null;
    this._reg = registrationService || null;
    this._disc = discoveryService || null;
    this._res = resolutionService || null;
    this._act = activationService || null;
    this._cmd = journeyCommands || null;
    this._persistence = persistenceService;
    this._ownershipProvider = ownershipProvider;
    this._obs = observability instanceof SupplierJourneyObservability
      ? observability : new SupplierJourneyObservability();
    this._now = nowFn;
    // Founder Ruling S3 — multiple concurrent independent objectives
    // are represented by distinct instance identities. We record each
    // instance's metadata here; the shared engine slot itself only
    // holds one JourneyContext per journeyId (per the protected engine
    // contract), but the orchestrator can dispatch across instances.
    this._instances = new Map(); // instanceId -> instance metadata
    this._registered = false;
  }

  get enabled() { return this._enabled; }
  get observability() { return this._obs; }
  static get JOURNEY_ID() { return SUPPLIER_JOURNEY_ID; }
  static get JOURNEY_VERSION() { return SUPPLIER_JOURNEY_VERSION; }

  /**
   * Register the enriched v2 supplier definition via RegistrationService.
   * Uses replace() when a lower version is already registered,
   * register() when nothing exists yet.
   */
  register() {
    _requireEnabled(this);
    const def = buildSupplierDefinition({ version: SUPPLIER_JOURNEY_VERSION });
    const status = this._reg.getRegistrationStatus(SUPPLIER_JOURNEY_ID);
    if (status && status.journeyVersion < SUPPLIER_JOURNEY_VERSION) {
      this._reg.replace(def);
    } else if (!status) {
      this._reg.register(def);
    }
    this._registered = true;
    this._obs.emit(SUPPLIER_JOURNEY_EVENTS.SUPPLIER_JOURNEY_REGISTRATION_CONFIRMED, {
      journeyId: SUPPLIER_JOURNEY_ID, journeyVersion: SUPPLIER_JOURNEY_VERSION,
    });
    return def;
  }

  /**
   * Evaluate ownership at runtime (S7). Returns a typed, auditable
   * outcome. Fail-closed on missing inputs or provider errors.
   */
  evaluateOwnership({ principalRef, organisationRef }) {
    _requireEnabled(this);
    const outcome = evaluateOwnership({
      principalRef, organisationRef,
      provider: this._ownershipProvider,
      nowFn: this._now,
    });
    this._obs.emit(SUPPLIER_JOURNEY_EVENTS.SUPPLIER_JOURNEY_OWNERSHIP_EVALUATED, {
      principalRef, organisationRef, decision: outcome.decision,
    });
    return outcome;
  }

  /**
   * Build a Supplier Resolution Request. `sourcingObjective` on the
   * buyer side maps to `supplierObjectiveRef` on the supplier side —
   * they are distinct per-journey shapes.
   */
  buildResolutionRequest({
    requestId, principalRef, organisationRef,
    supplierObjectiveRef, correlationRef = null,
    discoveryContext = {},
  }) {
    validateSupplierContext(discoveryContext);
    const instanceId = buildSupplierInstanceId({
      principalRef, organisationRef, supplierObjectiveRef, correlationRef,
    });
    return {
      requestId,
      correlationRef,
      canonReference: 'AJIP-16.02.008',
      journeyId: SUPPLIER_JOURNEY_ID,
      journeyVersion: SUPPLIER_JOURNEY_VERSION,
      principalContext: { principalRef },
      discoveryContext: {
        authenticated: true,
        permissions: [],
        activeFeatureFlags: ['JOURNEYS_ENABLED'],
        audience: 'authorised-supplier',
        completedJourneys: [],
        activeJourneys: [],
        // S7 — organisation reference is context data, not authority.
        organisationRef,
        ...discoveryContext,
      },
      entryPoint: 'internal-orchestration',
      requiredFeatureFlags: ['JOURNEYS_ENABLED'],
      activationIntent: true,
      metadata: {
        supplierObjectiveRef,
        organisationRef,
        instanceId,
      },
    };
  }

  /**
   * Activate a Supplier Journey instance for a specific objective.
   * Founder Ruling S7 — ownership authorisation is REQUIRED prior to
   * activation. Failing ownership fails activation closed with typed
   * outcome; no engine mutation occurs.
   */
  async activate({
    requestId, principalRef, organisationRef,
    supplierObjectiveRef, correlationRef = null,
    discoveryContext = {},
  }) {
    _requireEnabled(this);
    this._obs.emit(SUPPLIER_JOURNEY_EVENTS.SUPPLIER_JOURNEY_ACTIVATION_REQUESTED, {
      requestId, principalRef, organisationRef, supplierObjectiveRef,
    });
    // Runtime ownership gate (S7). Fail-closed.
    const ownership = this.evaluateOwnership({ principalRef, organisationRef });
    if (ownership.decision !== OWNERSHIP_DECISIONS.AUTHORISED) {
      this._obs.emit(SUPPLIER_JOURNEY_EVENTS.SUPPLIER_JOURNEY_ACTIVATION_REJECTED, {
        requestId, reason: 'ownership-not-authorised',
        decision: ownership.decision,
      });
      return Object.freeze({
        status: 'REJECTED',
        reason: 'ownership-not-authorised',
        ownership,
      });
    }
    const request = this.buildResolutionRequest({
      requestId, principalRef, organisationRef,
      supplierObjectiveRef, correlationRef, discoveryContext,
    });
    const result = await this._act.activate(request);
    if (result.status === 'ACTIVATED' || result.status === 'RESUMED' || result.status === 'RESTORED') {
      const instanceId = request.metadata.instanceId;
      this._instances.set(instanceId, {
        principalRef, organisationRef, supplierObjectiveRef, correlationRef,
        currentStage: SUPPLIER_STAGES.ONBOARDING_INITIATED,
      });
      // Establish initial stage via engine.updateStage() only.
      this._engine.updateStage(SUPPLIER_JOURNEY_ID, SUPPLIER_STAGES.ONBOARDING_INITIATED);
      this._obs.emit(SUPPLIER_JOURNEY_EVENTS.SUPPLIER_JOURNEY_ACTIVATED, {
        requestId, instanceId, initialStage: SUPPLIER_STAGES.ONBOARDING_INITIATED,
      });
    } else {
      this._obs.emit(SUPPLIER_JOURNEY_EVENTS.SUPPLIER_JOURNEY_ACTIVATION_REJECTED, {
        requestId, status: result.status, reason: result.reason,
      });
    }
    return result;
  }

  /**
   * Advance stage via deterministic supplier transition table. Uses
   * engine.updateStage() only (never JourneyContext._setStage()).
   */
  advanceStage({ supplierEvent, requestId = null }) {
    _requireEnabled(this);
    const ctx = this._engine.contextOf(SUPPLIER_JOURNEY_ID);
    if (!ctx) {
      throw new SupplierJourneyNotActiveError(
        `Supplier journey '${SUPPLIER_JOURNEY_ID}' is not active`,
      );
    }
    const fromStage = ctx.currentStage || SUPPLIER_STAGES.ONBOARDING_INITIATED;
    const transition = findSupplierTransition(fromStage, supplierEvent);
    if (!transition) {
      throw new InvalidSupplierTransitionError(
        `No supplier transition from '${fromStage}' on event '${supplierEvent}'`,
      );
    }
    if (transition.engineTransition) {
      switch (transition.engineTransition) {
        case 'wait': this._engine.suspend(SUPPLIER_JOURNEY_ID); break;
        case 'resume': this._engine.resume(SUPPLIER_JOURNEY_ID); break;
        case 'complete': this._engine.complete(SUPPLIER_JOURNEY_ID); break;
        default: break;
      }
    }
    this._engine.updateStage(SUPPLIER_JOURNEY_ID, transition.to);
    this._obs.emit(SUPPLIER_JOURNEY_EVENTS.SUPPLIER_JOURNEY_STAGE_TRANSITIONED, {
      requestId, fromStage, toStage: transition.to, supplierEvent,
      engineState: STAGE_TO_ENGINE_STATE[transition.to],
      persist: Boolean(transition.persist),
    });
    return Object.freeze({
      journeyId: SUPPLIER_JOURNEY_ID,
      fromStage, toStage: transition.to,
      supplierEvent,
      engineState: STAGE_TO_ENGINE_STATE[transition.to],
    });
  }

  /**
   * File a verification outcome (S4). Delegates SHAPE validation and
   * frozen-record construction to the Trust Boundary. The journey
   * NEVER manufactures verification truth or scores.
   */
  recordVerificationOutcome(payload) {
    _requireEnabled(this);
    return recordVerificationOutcome(payload);
  }

  complete({ requestId, principalRef, correlationRef = null }) {
    _requireEnabled(this);
    const r = this._cmd.complete({
      requestId, journeyId: SUPPLIER_JOURNEY_ID, principalRef, correlationRef,
    });
    this._obs.emit(SUPPLIER_JOURNEY_EVENTS.SUPPLIER_JOURNEY_COMPLETED, { requestId, result: r });
    return r;
  }

  cancel({ requestId, principalRef, correlationRef = null }) {
    _requireEnabled(this);
    const r = this._cmd.cancel({
      requestId, journeyId: SUPPLIER_JOURNEY_ID, principalRef, correlationRef,
    });
    this._obs.emit(SUPPLIER_JOURNEY_EVENTS.SUPPLIER_JOURNEY_CANCELLED, { requestId, result: r });
    return r;
  }

  isRegistered() { return this._registered; }
  hasInstance(instanceId) { return this._instances.has(instanceId); }
  instanceCount() { return this._instances.size; }
}

export {
  SupplierJourneyOrchestrator,
  buildSupplierInstanceId,
  SUPPLIER_JOURNEY_ID,
  SUPPLIER_JOURNEY_VERSION,
};
